import React, { useState } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { savePlayer, deletePlayer, deleteGame, fetchAllGames, adminMigrateToSeasons, adminStartNewSeason, fetchSeasonConfig, adminGrantTestXp, adminResetPassProgress } from '../utils/firebase';
import { getRank, compositeScore } from '../utils/helpers';
import { BASE_ELO } from '../constants/ranks';
import { useToast } from './ToastContext';

function AdminPage({ players, onPlayersChange, isAdmin, seasonConfig, onSeasonConfigChange }) {
  const toast = useToast();
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState("");
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [showGames, setShowGames] = useState(false);
  const [deletingAllData, setDeletingAllData] = useState(false);

  async function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    const added = await savePlayer(trimmed);
    setMsg(added ? `\u2705 ${trimmed} added!` : `\u26a0\ufe0f ${trimmed} already exists.`);
    setNewName("");
    await onPlayersChange();
    setAdding(false);
    setTimeout(() => setMsg(""), 3000);
  }

  async function handleDeletePlayer(playerId, playerName) {
    if (!window.confirm(`Are you sure you want to delete ${playerName}? This cannot be undone.`)) {
      return;
    }
    await deletePlayer(playerId);
    setMsg(`\u2705 ${playerName} deleted.`);
    await onPlayersChange();
    setTimeout(() => setMsg(""), 3000);
  }

  async function loadGames() {
    setLoadingGames(true);
    const allGames = await fetchAllGames();
    setGames(allGames);
    setLoadingGames(false);
    setShowGames(true);
  }

  async function handleDeleteGame(gameId) {
    if (!window.confirm("Are you sure you want to delete this game? This cannot be undone.")) {
      return;
    }
    await deleteGame(gameId);
    setMsg("\u2705 Game deleted.");
    await loadGames();
    setTimeout(() => setMsg(""), 3000);
  }

  async function handleDeleteAllData() {
    const confirmText = "DELETE ALL DATA";
    const userInput = window.prompt(
      `\u26a0\ufe0f WARNING: This will DELETE ALL GAMES and RESET ALL PLAYER STATS!\n\n` +
      `WILL BE DELETED/RESET:\n` +
      `- All game records\n` +
      `- All ELO ratings (reset to ${BASE_ELO})\n` +
      `- All game statistics (kills, tasks, wins, losses)\n` +
      `- All StatTrak values (reset to 0)\n\n` +
      `WILL BE KEPT:\n` +
      `- Player names and claimed players\n` +
      `- Lootbox inventories and items\n` +
      `- Impostor and Crewmate tokens\n` +
      `- Marketplace listings\n` +
      `- User accounts and IGNs\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      toast.info("Action cancelled.");
      return;
    }

    setDeletingAllData(true);

    try {
      const fn = httpsCallable(functions, 'adminDeleteAllData');
      await fn();
      setMsg("\u2705 All game data deleted and stats reset!");
      await onPlayersChange();
      setGames([]);
      setShowGames(false);
    } catch (error) {
      console.error("Error deleting all data:", error);
      setMsg("\u274c Error deleting data. Check console.");
    }

    setDeletingAllData(false);
    setTimeout(() => setMsg(""), 5000);
  }

  async function handleResetAllElo() {
    const confirmText = "RESET ALL ELO";
    const userInput = window.prompt(
      `\u26a0\ufe0f WARNING: This will RESET ALL PLAYER ELO to ${BASE_ELO}!\n\n` +
      `This will set:\n` +
      `- All imposter ELO to ${BASE_ELO}\n` +
      `- All crewmate ELO to ${BASE_ELO}\n` +
      `- Game history and other stats will remain unchanged\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      toast.info("Action cancelled.");
      return;
    }

    setDeletingAllData(true);

    try {
      const fn = httpsCallable(functions, 'adminResetAllElo');
      const result = await fn();
      setMsg(`\u2705 Reset ELO to ${BASE_ELO} for ${result.data.count} players!`);
      await onPlayersChange();
    } catch (error) {
      console.error("Error resetting ELO:", error);
      setMsg("\u274c Error resetting ELO. Check console.");
    }

    setDeletingAllData(false);
    setTimeout(() => setMsg(""), 5000);
  }

  async function handleClearAllInventories() {
    const confirmText = "CLEAR ALL INVENTORIES";
    const userInput = window.prompt(
      `\u26a0\ufe0f WARNING: This will CLEAR ALL USER INVENTORIES!\n\n` +
      `This will remove:\n` +
      `- All lootbox items from all user inventories\n` +
      `- All equipped emojis\n` +
      `- User accounts will remain with claimed players and comparison history\n\n` +
      `This is useful for removing deprecated items after updates.\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      toast.info("Action cancelled.");
      return;
    }

    setDeletingAllData(true); // Reuse this state for loading indicator

    try {
      const fn = httpsCallable(functions, 'adminClearAllInventories');
      const result = await fn();
      setMsg(`\u2705 Cleared inventories for ${result.data.count} users!`);
    } catch (error) {
      console.error("Error clearing inventories:", error);
      setMsg("\u274c Error clearing inventories. Check console.");
    }

    setDeletingAllData(false);
    setTimeout(() => setMsg(""), 5000);
  }

  async function handleMigrateToSeasons() {
    if (!window.confirm('This will tag all existing data as Season 1 and create the season config. Safe to run multiple times. Proceed?')) return;

    setDeletingAllData(true);
    try {
      const result = await adminMigrateToSeasons();
      setMsg(`\u2705 Migration complete! Tagged ${result.gamesTagged} games, ${result.txTagged} transactions, ${result.playersTagged} players as Season 1.`);
      // Refresh season config
      const newConfig = await fetchSeasonConfig();
      if (onSeasonConfigChange) onSeasonConfigChange(newConfig);
    } catch (error) {
      console.error("Error migrating to seasons:", error);
      setMsg("\u274c Error migrating to seasons. Check console.");
    }
    setDeletingAllData(false);
    setTimeout(() => setMsg(""), 8000);
  }

  async function handleStartNewSeason() {
    const currentSeasonNum = seasonConfig?.currentSeason || 1;
    const newSeasonNum = currentSeasonNum + 1;
    const confirmText = "START NEW SEASON";
    const userInput = window.prompt(
      `\u26a0\ufe0f START SEASON ${newSeasonNum}\n\n` +
      `This will:\n` +
      `SNAPSHOT & RESET:\n` +
      `- All player ELO (reset to ${BASE_ELO})\n` +
      `- All game statistics (kills, tasks, wins, losses, streaks)\n` +
      `- All casino stats\n` +
      `- All unboxing stats\n` +
      `- All achievement progress\n` +
      `- All StatTrak values (reset to 0)\n\n` +
      `KEPT (carries over):\n` +
      `- All tokens (crewmate + impostor)\n` +
      `- All items, inventory, equipped cosmetics\n` +
      `- All lootbox stock\n` +
      `- Player names, accounts, IGNs\n\n` +
      `Season ${currentSeasonNum} data will be archived and viewable.\n` +
      `Games will be blocked during the transition (~10-30 seconds).\n\n` +
      `Type "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      toast.info("Action cancelled.");
      return;
    }

    setDeletingAllData(true);
    try {
      const result = await adminStartNewSeason();
      setMsg(`\u2705 Season ${result.newSeason} started! Archived ${result.playersSnapshot} players, ${result.casinoStatsSnapshot} casino stats, ${result.unboxingStatsSnapshot} unboxing stats from Season ${result.previousSeason}.`);
      await onPlayersChange();
      // Refresh season config
      const newConfig = await fetchSeasonConfig();
      if (onSeasonConfigChange) onSeasonConfigChange(newConfig);
    } catch (error) {
      console.error("Error starting new season:", error);
      setMsg("\u274c Error starting new season. Check console.");
    }
    setDeletingAllData(false);
    setTimeout(() => setMsg(""), 10000);
  }

  function formatDate(timestamp) {
    return new Date(timestamp).toLocaleString();
  }

  return (
    <div className="page-container">
      <h1 className="page-title">Player Roster</h1>

      {isAdmin ? (
        <>
          <div className="admin-add">
            <input
              className="name-input"
              type="text"
              placeholder="Enter player name..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <button className="btn-primary" onClick={handleAdd} disabled={adding}>
              {adding ? "Adding..." : "Add Player"}
            </button>
          </div>
          {msg && <div className="form-msg">{msg}</div>}
        </>
      ) : (
        <div className="admin-notice">
          <p>{"\ud83d\udd12"} Admin privileges required to add or remove players.</p>
        </div>
      )}

      <div className="player-roster">
        {players.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">{"\u2795"}</div>
            <h2>No Players Yet</h2>
            <p>Add players above to get started.</p>
          </div>
        )}
        {players.map((p) => {
          const pGameCount = games.filter(g =>
            g.impostors.includes(p.id) || (g.crewmates && g.crewmates.includes(p.id))
          ).length;
          const comp = compositeScore(p, pGameCount);
          const rank = getRank(comp ?? BASE_ELO);

          return (
            <div className="roster-row" key={p.id}>
              <div className="roster-name">{p.name}</div>
              <div className="roster-stats">
                <span>{"\ud83d\udd2a"} {Math.round(p.eloImposter)} ELO</span>
                <span>{"\ud83d\udd35"} {Math.round(p.eloCrewmate)} ELO</span>
                <span>{"\ud83d\ude80"} {Math.round(p.gameScore)} game pts ({p.gameCount} games)</span>
              </div>
              <div className="roster-rank" style={{ color: rank.color }}>
                <img src={rank.icon} alt={rank.name} className="rank-badge-img" /> {rank.name}
                {comp ? ` \u00b7 ${comp}` : " \u00b7 Unranked"}
              </div>
              {isAdmin && (
                <button
                  className="btn-delete"
                  onClick={() => handleDeletePlayer(p.id, p.name)}
                  title="Delete player"
                >
                  {"\ud83d\uddd1\ufe0f"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <>
          <div className="games-section">
            <h2 className="section-title">Game Management (Admin Only)</h2>
            <p className="admin-info">View game history on the Leaderboard page. Delete games here.</p>
            {!showGames ? (
              <button className="btn-primary" onClick={loadGames} disabled={loadingGames}>
                {loadingGames ? "Loading..." : "Load Games to Delete"}
              </button>
            ) : (
              <div className="games-list">
                {games.length === 0 ? (
                  <p>No games recorded yet.</p>
                ) : (
                  games.map((g) => (
                    <div key={g.id} className="game-row">
                      <div className="game-info">
                        <div className="game-outcome">
                          {g.didWin ? "\u2705 Impostors Won" : "\u274c Impostors Lost"}
                          {g.outcomeType && (
                            <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                              ({g.outcomeType === 'tasks' ? '\u2705 Tasks' : g.outcomeType === 'kills' ? '\ud83d\udd2a Kills' : g.outcomeType === 'sabotage' ? '\ud83d\udca3 Sabotage' : '\ud83d\uddf3\ufe0f Votes'})
                            </span>
                          )}
                        </div>
                        <div className="game-details">
                          <span>Impostors: {g.impostors.join(", ")}</span>
                          <span>Players: {g.totalPlayers}</span>
                          <span>Remaining: {g.playersRemaining}</span>
                          <span>Score {"\u0394"}: {g.scoreChange > 0 ? "+" : ""}{g.scoreChange}</span>
                        </div>
                        <div className="game-timestamp">{formatDate(g.timestamp)}</div>
                      </div>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteGame(g.id)}
                        title="Delete game"
                      >
                        {"\ud83d\uddd1\ufe0f"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Season Management */}
          <div style={{
            marginTop: '32px',
            padding: '20px',
            background: 'rgba(33, 150, 243, 0.05)',
            border: '2px solid #2196f3',
            borderRadius: '12px'
          }}>
            <h2 className="section-title" style={{ color: '#2196f3', marginBottom: '12px' }}>
              {"\ud83c\udfc6"} Season Management
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
              Current Season: <strong style={{ color: '#fff' }}>{seasonConfig?.currentSeason || 1}</strong>
              {seasonConfig?.seasons && Object.keys(seasonConfig.seasons).length > 1 && (
                <span> ({Object.keys(seasonConfig.seasons).length} seasons total)</span>
              )}
            </p>

            {/* Migrate to Seasons */}
            <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h3 style={{ color: '#4caf50', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83d\udce6"} Migrate to Seasons Framework
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Tags all existing games, casino transactions, and players with season: 1. Creates the season config document. Safe to run multiple times.
              </p>
              <button
                className="btn-primary"
                onClick={handleMigrateToSeasons}
                disabled={deletingAllData}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {deletingAllData ? "Migrating..." : "\ud83d\udce6 Migrate Existing Data to Season 1"}
              </button>
            </div>

            {/* Start New Season */}
            <div>
              <h3 style={{ color: '#ff9800', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83c\udf1f"} Start New Season
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Archives all current stats as Season {seasonConfig?.currentSeason || 1} and resets everything for Season {(seasonConfig?.currentSeason || 1) + 1}.
                Tokens carry over. Inventory is liquidated into crewmate tokens (rank rewards kept). ELO, game stats, casino stats, unboxing stats, achievements, and StatTrak values reset.
              </p>
              <button
                className="btn-delete"
                onClick={handleStartNewSeason}
                disabled={deletingAllData}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  background: '#e65100',
                  border: '1px solid #ff9800',
                }}
              >
                {deletingAllData ? "Starting Season..." : `\ud83c\udf1f Start Season ${(seasonConfig?.currentSeason || 1) + 1}`}
              </button>
            </div>

            {/* Season Status */}
            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Current Season: <strong style={{ color: '#4caf50' }}>
                  Season {seasonConfig?.currentSeason || 1}
                </strong>
              </p>

              {/* Test helpers — only for test@test.com */}
              <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '8px', fontSize: '13px' }}>Test Account Tools</h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={async () => {
                      try {
                        const result = await adminGrantTestXp(500);
                        toast.success(`+500 XP! Level ${result.newLevel}`);
                      } catch (err) {
                        toast.error(err.message || 'Failed');
                      }
                    }}
                    style={{ padding: '6px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
                  >
                    +500 XP
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const result = await adminGrantTestXp(5000);
                        toast.success(`+5000 XP! Level ${result.newLevel}`);
                      } catch (err) {
                        toast.error(err.message || 'Failed');
                      }
                    }}
                    style={{ padding: '6px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
                  >
                    +5000 XP
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Reset all pass progress for your test account?')) return;
                      try {
                        await adminResetPassProgress();
                        toast.success('Pass progress reset!');
                      } catch (err) {
                        toast.error(err.message || 'Failed');
                      }
                    }}
                    style={{ padding: '6px 12px', background: 'var(--bg-card)', border: '1px solid rgba(244, 67, 54, 0.3)', borderRadius: '6px', color: 'var(--red)', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Reset Pass Progress
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="danger-zone" style={{
            marginTop: '32px',
            padding: '20px',
            background: 'rgba(244, 67, 54, 0.05)',
            border: '2px solid var(--red)',
            borderRadius: '12px'
          }}>
            <h2 className="section-title" style={{ color: 'var(--red)', marginBottom: '12px' }}>
              {"\u26a0\ufe0f"} Danger Zone
            </h2>

            {/* Reset All ELO */}
            <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h3 style={{ color: 'var(--gold)', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83d\udd04"} Reset All Player ELO to 1000
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Reset all imposter and crewmate ELO ratings to 1000 for all players. Game history and other stats will remain unchanged. Useful for starting a new season.
              </p>
              <button
                className="btn-delete"
                onClick={handleResetAllElo}
                disabled={deletingAllData}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {deletingAllData ? "Resetting..." : "\ud83d\udd04 Reset All ELO"}
              </button>
            </div>

            {/* Clear All Inventories */}
            <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h3 style={{ color: 'var(--gold)', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83c\udf92"} Clear All User Inventories
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Remove all lootbox items and equipped emojis from all users. User accounts, claimed players, and comparison history will remain intact. Useful for removing deprecated items after updates.
              </p>
              <button
                className="btn-delete"
                onClick={handleClearAllInventories}
                disabled={deletingAllData}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {deletingAllData ? "Clearing..." : "\ud83e\uddf9 Clear All Inventories"}
              </button>
            </div>

            {/* One-Time Token Fix */}
            <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h3 style={{ color: '#4caf50', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83e\ude99"} Grant +5 Crewmate Tokens (One-Time Fix)
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Awards +5 crewmate tokens to ALL users to offset the Crewling weekly reward bug. Only run this once!
              </p>
              <button
                className="btn-delete"
                onClick={async () => {
                  if (!window.confirm('Grant +5 crewmate tokens to ALL users? Only run once!')) return;
                  setDeletingAllData(true);
                  try {
                    const fn = httpsCallable(functions, 'adminGrantTokenFix');
                    const result = await fn();
                    setMsg(`\u2705 Granted +5 crewmate tokens to ${result.data.usersUpdated} users!`);
                  } catch (error) {
                    console.error("Error granting tokens:", error);
                    setMsg("\u274c Error granting tokens. Check console.");
                  }
                  setDeletingAllData(false);
                }}
                disabled={deletingAllData}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  background: '#2e7d32',
                  border: '1px solid #4caf50',
                }}
              >
                {deletingAllData ? "Granting..." : "\ud83e\ude99 Grant +5 Tokens to All"}
              </button>
            </div>

            {/* Delete All Data */}
            <div>
              <h3 style={{ color: 'var(--red)', marginBottom: '8px', fontSize: '16px' }}>
                {"\ud83d\udc80"} Delete All Game Data
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
                Delete ALL game records and reset ALL player statistics to initial values. Player names will be kept. This action cannot be undone!
              </p>
              <button
                className="btn-delete"
                onClick={handleDeleteAllData}
                disabled={deletingAllData}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                {deletingAllData ? "Deleting All Data..." : "\ud83d\uddd1\ufe0f Delete All Data"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPage;
